/**
 * SignalR Type Definitions for CSReportingWebService
 * 
 * These types match the server-side SignalR event payloads sent from
 * the .NET 9 CSReportingWebService API.
 * 
 * Hub Endpoint: /hubs/reportList
 * Authentication: JWT Bearer token required
 */

// ============================================================================
// Base Event Payloads
// ============================================================================

/**
 * Payload for ReportList events (Created, Updated, Deleted)
 */
export interface ReportListEventPayload {
  /** Unique identifier for the report list */
  reportListKey: string
  /** Tenant ID that owns this report list */
  tenantId: number
  /** ISO 8601 timestamp when event occurred (UTC) */
  timestamp: string
}

/**
 * Payload for single ReportListItem events (Created, Updated, Deleted)
 */
export interface ReportListItemEventPayload {
  /** Unique identifier for the report list item */
  reportListItemKey: string
  /** Parent report list identifier */
  reportListKey: string
  /** Tenant ID that owns this item */
  tenantId: number
  /** ISO 8601 timestamp when event occurred (UTC) */
  timestamp: string
}

/**
 * Payload for multiple ReportListItem events (Batch Created, Batch Deleted)
 */
export interface MultipleReportListItemsEventPayload {
  /** Array of report list item identifiers */
  reportListItemKeys: string[]
  /** Parent report list identifier */
  reportListKey: string
  /** Tenant ID that owns these items */
  tenantId: number
  /** ISO 8601 timestamp when event occurred (UTC) */
  timestamp: string
  /** Number of items affected */
  count: number
}

// ============================================================================
// Event Handler Function Types
// ============================================================================

export type ReportListCreatedHandler = (data: ReportListEventPayload) => void
export type ReportListUpdatedHandler = (data: ReportListEventPayload) => void
export type ReportListDeletedHandler = (data: ReportListEventPayload) => void

export type ReportListItemCreatedHandler = (
  data: ReportListItemEventPayload,
) => void
export type ReportListItemUpdatedHandler = (
  data: ReportListItemEventPayload,
) => void
export type ReportListItemDeletedHandler = (
  data: ReportListItemEventPayload,
) => void

export type MultipleReportListItemsCreatedHandler = (
  data: MultipleReportListItemsEventPayload,
) => void
export type MultipleReportListItemsDeletedHandler = (
  data: MultipleReportListItemsEventPayload,
) => void

// ============================================================================
// SignalR Event Handler Map
// ============================================================================

/**
 * Complete map of all SignalR event names to their handler functions.
 * Use this type when registering multiple event handlers.
 */
export interface SignalREventHandlers {
  /** Fired when a new report list is created */
  ReportListCreated?: ReportListCreatedHandler
  /** Fired when a report list is updated */
  ReportListUpdated?: ReportListUpdatedHandler
  /** Fired when a report list is deleted */
  ReportListDeleted?: ReportListDeletedHandler
  /** Fired when a single report list item is created */
  ReportListItemCreated?: ReportListItemCreatedHandler
  /** Fired when a single report list item is updated */
  ReportListItemUpdated?: ReportListItemUpdatedHandler
  /** Fired when a single report list item is deleted */
  ReportListItemDeleted?: ReportListItemDeletedHandler
  /** Fired when multiple report list items are created in batch */
  MultipleReportListItemsCreated?: MultipleReportListItemsCreatedHandler
  /** Fired when multiple report list items are deleted in batch */
  MultipleReportListItemsDeleted?: MultipleReportListItemsDeletedHandler
}

// ============================================================================
// Connection State
// ============================================================================

/**
 * SignalR connection state matching HubConnectionState
 */
export const SignalRConnectionState = {
  /** Connection is being established */
  Connecting: "Connecting",
  /** Connection is active and ready to send/receive messages */
  Connected: "Connected",
  /** Connection is in the process of reconnecting */
  Reconnecting: "Reconnecting",
  /** Connection is closed */
  Disconnected: "Disconnected",
} as const

export type SignalRConnectionState =
  (typeof SignalRConnectionState)[keyof typeof SignalRConnectionState]

// ============================================================================
// Hook Configuration
// ============================================================================

/**
 * Configuration options for the useReportListSignalR hook
 */
export interface UseReportListSignalROptions {
  /** Optional callback handlers for SignalR events */
  eventHandlers?: SignalREventHandlers
  /** Whether to automatically connect on mount (default: true) */
  autoConnect?: boolean
  /** Enable detailed logging for debugging (default: false in production) */
  enableLogging?: boolean
  /** Custom hub URL (overrides default from env) */
  hubUrl?: string
}

/**
 * Return value from useReportListSignalR hook
 */
export interface UseReportListSignalRReturn {
  /** Current connection state */
  connectionState: SignalRConnectionState
  /** Whether currently connected */
  isConnected: boolean
  /** Whether currently connecting or reconnecting */
  isConnecting: boolean
  /** Last error that occurred */
  error: Error | null
  /** Manually trigger reconnection */
  reconnect: () => Promise<void>
  /** Manually disconnect */
  disconnect: () => Promise<void>
}
